import type { DefineLocaleMessage } from 'vue-i18n';

import { useConfigStore } from '~/stores/config';

type SupportedLocale = 'en-US' | 'en-AU';

const isValidLocale = (locale: string | undefined): locale is SupportedLocale => {
  return locale === 'en-US' || locale === 'en-AU';
};

// Each entry is one JSON bundle. The loader calls `mergeLocaleMessage` per bundle,
// so adding a new bundle is a one-line change and bundles deep-merge with backend keys
// (no shallow-spread collision on shared top-level namespaces like `schools`).
const localeImports: Record<SupportedLocale, Array<() => Promise<{ default: Record<string, unknown> }>>> = {
  'en-US': [
    () => import('~i18n/locales/en-US/unified.json'),
    () => import('~i18n/locales/en-US/schools.json'),
    () => import('~i18n/locales/en-US/coach.json'),
    () => import('~i18n/locales/en-US/kui.json'),
  ],
  // en-AU is a sparse override set: a domain with nothing to override has no file here, and
  // every one of its keys falls back to en-US.
  'en-AU': [
    () => import('~i18n/locales/en-AU/unified.json'),
    () => import('~i18n/locales/en-AU/schools.json'),
    () => import('~i18n/locales/en-AU/coach.json'),
  ],
};

const loadLocaleBundles = async (locale: SupportedLocale): Promise<Array<Record<string, unknown>>> => {
  return Promise.all(localeImports[locale].map(load => load().then(m => m.default)));
};

export default defineNuxtPlugin({
  name: 'load-config',
  dependsOn: [],
  setup: async () => {
    const configStore = useConfigStore();

    if (import.meta.server) {
      await configStore.loadConfig();
    }

    const { $i18n } = useNuxtApp();

    const clusterLocale = configStore.publicConfig?.country?.defaultLocale;
    const appLocale = isValidLocale(clusterLocale) ? clusterLocale : 'en-US';

    const serverMessages = configStore.i18nMessages || {};

    const applyMessages = async (locale: SupportedLocale) => {
      // Each locale must get its OWN copy: vue-i18n stores the object by reference
      // and mergeLocaleMessage mutates it in place, so sharing one serverMessages
      // object across locales lets the en-US bundle merge overwrite the en-AU
      // overrides inside the shared object (both locales end up identical).
      // JSON round-trip (not structuredClone) — serverMessages is a reactive
      // store proxy, which structuredClone cannot clone.
      const messagesCopy = JSON.parse(JSON.stringify(serverMessages)) as DefineLocaleMessage;
      $i18n.setLocaleMessage(locale, messagesCopy);
      // en-US is the source of truth and regional locales are sparse overrides, so a
      // regional locale layers the en-US bundles first. Without them a key that lives in
      // both the backend lang files and the frontend JSON keeps the backend wording here:
      // it resolves inside the regional locale, so the en-US fallback is never consulted.
      const bundleLocales: SupportedLocale[] = locale === 'en-US' ? ['en-US'] : ['en-US', locale];
      for (const bundleLocale of bundleLocales) {
        try {
          const bundles = await loadLocaleBundles(bundleLocale);
          for (const bundle of bundles) {
            $i18n.mergeLocaleMessage(locale, bundle as unknown as DefineLocaleMessage);
          }
        }
        catch (e) {
          console.error(`[i18n] Failed to load ${bundleLocale} translations`, e);
        }
      }
    };

    await applyMessages(appLocale);

    // Redundant while the regional merge above succeeds (its tree already contains the
    // en-US bundles); kept deliberately as the fallback tree for when a bundle load fails.
    if (appLocale !== 'en-US') {
      await applyMessages('en-US');
    }

    $i18n.locale.value = appLocale;
  },
});
